import React, { useState } from 'react';
import { Upload, message, Modal, Image, Typography, Spin } from 'antd';
import { 
  PictureOutlined, 
  DeleteOutlined, 
  LoadingOutlined,
  InboxOutlined
} from '@ant-design/icons';
import ImageService from '../services/imageService';

const { Text, Title } = Typography;
const { Dragger } = Upload;

/**
 * 图片上传组件
 * 支持图片上传、预览和删除
 * @param {Object} props 组件属性
 * @param {Array} props.value 当前图片列表
 * @param {Function} props.onChange 值变化时的回调函数
 * @param {number} props.maxCount 最大上传数量
 * @param {boolean} props.disabled 是否禁用
 */
function ImageUploader({ value = [], onChange, maxCount = 5, disabled = false }) {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [fileList, setFileList] = useState(value.map((item, index) => ({
    uid: item.uid || `-${index}`,
    name: item.name || `图片 ${index + 1}`,
    status: 'done',
    url: item.url,
    key: item.key,
  })));
  const [uploading, setUploading] = useState(false);

  // 处理图片预览
  const handlePreview = async (file) => {
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  // 处理图片变更
  const handleChange = ({ file, fileList }) => {
    // 更新内部状态
    setFileList(fileList);
    
    // 处理上传完成的文件
    if (file.status === 'done') {
      const newFileList = fileList.map(f => {
        if (f.uid === file.uid) {
          return {
            uid: f.uid,
            name: f.name,
            url: f.response?.url || f.url,
            key: f.response?.key || f.key,
          };
        }
        return {
          uid: f.uid,
          name: f.name,
          url: f.response?.url || f.url,
          key: f.response?.key || f.key,
        };
      });
      
      // 触发外部onChange
      if (onChange) {
        onChange(newFileList);
      }
    } else if (file.status === 'removed') {
      // 处理删除，外部onChange已在handleRemove中处理
    } else if (file.status === 'error') {
      message.error(`${file.name} 上传失败`);
    }
  };

  // 自定义上传操作
  const customRequest = async (options) => {
    const { file, onProgress, onSuccess, onError } = options;
    
    // 验证文件类型
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
      onError(new Error('只能上传图片文件'));
      return;
    }
    
    // 验证文件大小（限制为5MB）
    const isLtLimit = file.size / 1024 / 1024 < 5;
    if (!isLtLimit) {
      message.error('图片大小不能超过5MB!');
      onError(new Error('图片大小不能超过5MB'));
      return;
    }
    
    setUploading(true);
    
    try {
      // 调用图片服务上传，处理进度回调
      const progressCallback = (percent) => {
        if (onProgress) {
          onProgress({ percent });
        }
      };
      
      // 调用图片服务上传
      const result = await ImageService.uploadImage(file, progressCallback);
      
      onSuccess(result, file);
      message.success(`${file.name} 上传成功`);
    } catch (error) {
      console.error('上传失败:', error);
      onError(error);
      message.error(`${file.name} 上传失败: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // 处理图片删除
  const handleRemove = async (file) => {
    try {
      if (file.key) {
        await ImageService.deleteImage(file.key);
        
        // 更新文件列表并触发外部onChange
        const newFileList = fileList.filter(item => item.uid !== file.uid);
        setFileList(newFileList);
        
        if (onChange) {
          onChange(newFileList);
        }
        
        message.success(`${file.name} 删除成功`);
      }
      return true;
    } catch (error) {
      console.error('删除失败:', error);
      message.error(`${file.name} 删除失败: ${error.message}`);
      return false;
    }
  };

  // 上传按钮
  const uploadButton = (
    <div>
      <PictureOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
      <div style={{ marginTop: 8 }}>上传图片</div>
    </div>
  );

  return (
    <div className="image-uploader-container">
      <Upload
        listType="picture-card"
        fileList={fileList}
        onPreview={handlePreview}
        onChange={handleChange}
        onRemove={handleRemove}
        customRequest={customRequest}
        disabled={disabled || uploading}
        maxCount={maxCount}
        multiple={maxCount > 1}
        accept="image/*"
        className="image-uploader"
      >
        {fileList.length >= maxCount ? null : uploadButton}
      </Upload>
      
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <Image
          alt="预览图片"
          style={{ width: '100%' }}
          src={previewImage}
        />
      </Modal>
      
      {/* 大屏幕上传控件 */}
      <div className="upload-dragger-container" style={{ display: fileList.length > 0 ? 'none' : 'block' }}>
        <Dragger
          customRequest={customRequest}
          showUploadList={false}
          disabled={disabled || uploading || fileList.length >= maxCount}
          accept="image/*"
          style={{ marginTop: 16 }}
          className="upload-dragger"
          onChange={handleChange}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: '#1890ff', fontSize: 48 }} />
          </p>
          <Title level={5}>点击或拖拽图片到此处上传</Title>
          <Text type="secondary">支持单个或批量上传，每张图片不超过5MB</Text>
          {uploading && (
            <div style={{ marginTop: 16 }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
              <div style={{ marginTop: 8 }}>上传中...</div>
            </div>
          )}
        </Dragger>
      </div>
      
      {/* 添加一些提示和辅助说明 */}
      {fileList.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            <span>点击图片可以预览，点击 </span>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            <span> 可以删除</span>
          </Text>
        </div>
      )}
    </div>
  );
}

export default ImageUploader;
